/**
 * Table of Contents Generator
 * 
 * Automatically generates a table of contents from page headings
 */

export class TOCGenerator {
  private container: HTMLElement | null;
  private contentArea: HTMLElement | null;

  constructor(tocContainerId: string = 'toc-nav', contentSelector: string = '.prose') {
    this.container = document.getElementById(tocContainerId);
    this.contentArea = document.querySelector(contentSelector);
  }

  /**
   * Generate and render the TOC
   */
  public generate(): void {
    if (!this.container || !this.contentArea) return;

    const headings = this.getHeadings();
    if (headings.length === 0) {
      this.container.innerHTML = '<p style="color: var(--color-text-light); font-size: 0.8rem;">No headings found</p>';
      return;
    }

    const tocHTML = this.buildTOCHTML(headings);
    this.container.innerHTML = tocHTML;

    this.attachScrollSpy();
  }

  /**
   * Get all h2 and h3 headings from content
   */
  private getHeadings(): Array<{ id: string; text: string; level: number }> {
    const headings = this.contentArea?.querySelectorAll('h2, h3') || [];
    const result: Array<{ id: string; text: string; level: number }> = [];

    headings.forEach((heading, index) => {
      const level = parseInt(heading.tagName.substring(1));
      let id = heading.id;

      // Generate ID if not present
      if (!id) {
        id = `heading-${index}`;
        heading.id = id;
      }

      result.push({
        id,
        text: heading.textContent || '',
        level,
      });
    });

    return result;
  }

  /**
   * Build TOC HTML
   */
  private buildTOCHTML(headings: Array<{ id: string; text: string; level: number }>): string {
    let html = '<ul>';
    let currentLevel = 2;

    headings.forEach((heading) => {
      if (heading.level > currentLevel) {
        html += '<ul>';
      } else if (heading.level < currentLevel) {
        html += '</ul>';
      }

      html += `<li><a href="#${heading.id}" class="toc-link" data-target="${heading.id}">${heading.text}</a></li>`;
      currentLevel = heading.level;
    });

    html += '</ul>';
    return html;
  }

  /**
   * Attach scroll spy to highlight current section
   */
  private attachScrollSpy(): void {
    const links = this.container?.querySelectorAll('.toc-link') || [];
    if (links.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.id;
          const link = this.container?.querySelector(`[data-target="${id}"]`);

          if (entry.isIntersecting) {
            // Remove active from all links
            links.forEach((l) => l.classList.remove('active'));
            // Add active to current link
            link?.classList.add('active');
          }
        });
      },
      {
        rootMargin: '-100px 0px -66%',
        threshold: 0,
      }
    );

    // Observe all headings
    const headings = this.contentArea?.querySelectorAll('h2, h3') || [];
    headings.forEach((heading) => observer.observe(heading));

    // Smooth scroll on click
    links.forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const targetId = (link as HTMLElement).dataset.target;
        const target = document.getElementById(targetId || '');
        
        if (target) {
          const offset = 100; // Account for fixed header
          const targetPosition = target.offsetTop - offset;
          
          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth',
          });
        }
      });
    });
  }
}

